/**
 * LocalStorage wrapper utilities
 * Provides type-safe access to localStorage with error handling
 *
 * Auth tokens are now managed via HTTP-only cookies (set by the backend).
 * This module only stores a non-sensitive session flag used as a hint
 * to know whether to attempt an auth check on page load.
 */

const SESSION_AUTHENTICATED_KEY = 'ep-session-authenticated';

// Legacy keys -- cleaned up during clearSessionAuthenticated()
const LEGACY_AUTH_TOKEN_KEY = 'ep-auth-token';
const LEGACY_REFRESH_TOKEN_KEY = 'ep-refresh-token';
const LEGACY_AUTH_KEY = 'ep-auth';

export const localStorageUtils = {
  /**
   * Mark that a session is active (user just logged in or completed verification).
   * This is NOT a credential -- just a hint for the frontend to try calling /api/me.
   */
  setSessionAuthenticated: (): void => {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(SESSION_AUTHENTICATED_KEY, 'true');
      }
    } catch (error) {
      console.error('Failed to set session flag:', error);
    }
  },

  /**
   * Check if a session might be authenticated (should we attempt auth check on load?)
   */
  isSessionAuthenticated: (): boolean => {
    try {
      if (typeof window !== 'undefined') {
        return window.localStorage.getItem(SESSION_AUTHENTICATED_KEY) === 'true';
      }
      return false;
    } catch (error) {
      console.error('Failed to read session flag:', error);
      return false;
    }
  },

  /**
   * Clear session flag and any legacy auth data
   */
  clearSessionAuthenticated: (): void => {
    try {
      if (typeof window !== 'undefined') {
        window.localStorage.removeItem(SESSION_AUTHENTICATED_KEY);
        // Clean up legacy keys from before the cookie migration
        window.localStorage.removeItem(LEGACY_AUTH_TOKEN_KEY);
        window.localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
        window.localStorage.removeItem(LEGACY_AUTH_KEY);
      }
    } catch (error) {
      console.error('Failed to clear session data:', error);
    }
  },
};
